"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { elapsed, elapsedMinutos, fecha_actual } from "../hooks/elapsed";
import Button from "./ui/Button";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  action?: (item: any) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  action,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Función para limpiar el JSX y manejar los renderizados especiales por nombre de columna
  const renderCellContent = (cell: any) => {
    const headerName = typeof cell.column.columnDef.header === "string"
      ? cell.column.columnDef.header
      : "";
    const p = cell.row.original as any;

    switch (headerName) {
      case "Action":
        return (
          <Button
            className="h-8 px-3 text-[11px] font-semibold"
            onClick={() => action && action(p)}
          >
            {p.action ? p.action : "Ver Más"}
          </Button>
        );
      case "VALIDAR":
        return (
          <Button
            className="h-8 px-3 text-[11px] font-semibold"
            onClick={() => action && action(p)}
          >
            {p.action ? p.action : "Validar"}
          </Button>
        );
      case "PDF":
        return (
          <a
            className="inline-flex items-center justify-center gap-1 rounded bg-brand-600 px-2.5 py-1 text-[10px] font-bold text-white shadow-sm transition-colors hover:bg-brand-700"
            href={p.p_fc_url_poliza}
            target="_blank"
            rel="noreferrer"
          >
            VER PDF
          </a>
        );
      case "Tiempo":
        return elapsedMinutos(p.ttes_fi_minutos_transcurridos);
      case "Tiempo T":
        return elapsed(p.sem_fd_fecha_inicial);
      case "Fecha Envio":
        return fecha_actual(p.fd_fecha_envio);
      case "Fecha Inicial":
        return fecha_actual(p.ttes_fd_fecha_inicial);
      default:
        // Renderizado estándar de TanStack Table
        return flexRender(cell.column.columnDef.cell, cell.getContext());
    }
  };

  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <Table className="w-full text-sm">
          <TableHeader className="bg-slate-50/80">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b border-slate-200 hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="h-11 px-4 text-left align-middle text-[11px] font-bold uppercase tracking-wider text-slate-500"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="divide-y divide-slate-100">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="transition-colors hover:bg-slate-50/60 data-[state=selected]:bg-slate-50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-4 py-3 text-xs font-medium text-slate-700 align-middle">
                      {renderCellContent(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-32 text-center align-middle">
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <svg className="mb-2 h-8 w-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                    </svg>
                    <span className="text-sm font-medium">No se encontraron registros</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
